import { useState, useEffect } from 'react';
import { API_URL, SITE_URL } from '../config';
import Spinner from '../components/Spinner';
import PhotoCarousel from '../components/PhotoCarousel';
import colors from '../theme/colors';

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return capitalizeFirst(new Date(dateStr).toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).replace(', ', ' '));
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const date = capitalizeFirst(d.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).replace(', ', ' '));
  const time = d.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `${date} alle ${time}`;
};

export default function EmbedHikePage({ hikeId }) {
  const [hike, setHike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHike = async () => {
      try {
        const response = await fetch(`${API_URL}/hikes/embed-page/${hikeId}`);
        if (!response.ok) {
          throw new Error('Gita non trovata');
        }
        const data = await response.json();
        setHike(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (hikeId) {
      fetchHike();
    }
  }, [hikeId]);

  const getTagColor = (tag) => {
    const tagColors = colors.tags[tag] || colors.tags.default;
    return `${tagColors.bg} ${tagColors.text}`;
  };

  const baseUrl = SITE_URL || window.location.origin.replace(/\/embed.*/, '');
  const detailUrl = hike?.is_scheda_hidden ? baseUrl : `${baseUrl}/hike/${hikeId}`;
  const registerUrl = `${baseUrl}/guest-register/${hikeId}`;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center h-64">
        <Spinner size="md" color="blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center h-64">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  if (!hike) {
    return null;
  }

  const hasCosts = hike.quota_cai || hike.quota_assicurazione || hike.quota_pernotto;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Hero image - bigger */}
      <div className="relative">
        {hike.photos && hike.photos.length > 0 ? (
          <PhotoCarousel
            photos={hike.photos}
            className="w-full h-56"
          />
        ) : hike.url_foto && hike.url_foto.trim() !== '' ? (
          <a href={detailUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={hike.url_foto}
              alt={hike.titolo}
              className="w-full h-56 object-cover"
              loading="lazy"
            />
          </a>
        ) : (
          <div className="w-full h-56 bg-gray-100 flex items-center justify-center">
            <p className="text-gray-400 text-sm">Nessuna foto</p>
          </div>
        )}

        {/* Badges overlay */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {hike.is_cancelled && (
            <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white shadow">
              Annullata
            </span>
          )}
        </div>
      </div>

      {/* Title, info, and description */}
      <div className="p-4">
        {/* Title */}
        <a
          href={detailUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block mb-1"
        >
          <h1 className="text-xl font-bold text-gray-800 leading-tight hover:text-blue-600 transition">
            {hike.titolo}
          </h1>
        </a>

        {hike.organizers && hike.organizers.length > 0 && (
          <p className="text-sm text-gray-500 mb-2">
            {hike.organizers.map(org => org.display_name).join(', ')}
          </p>
        )}

        {/* Tags */}
        {hike.tags && hike.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {hike.tags.map((tag, index) => (
              <span
                key={index}
                className={`px-2 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Date and location */}
        <div className="mb-3 space-y-1 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <span>📅</span>
            <span>
              {formatDateTime(hike.data_inizio)}
              {hike.data_fine && ` - ${formatDateTime(hike.data_fine)}`}
            </span>
          </div>
          {hike.luogo && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>📍</span>
              <span>{hike.luogo}</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-100 text-xs">
          {hike.data_apertura_iscrizioni && (
            <div>
              <p className="text-gray-500">Apertura iscrizioni</p>
              <p className="font-medium text-gray-800">{formatDate(hike.data_apertura_iscrizioni)}</p>
            </div>
          )}
          <div>
            <p className="text-gray-500">Termine iscrizioni</p>
            <p className="font-medium text-gray-800">{formatDate(hike.data_termine_iscrizioni)}</p>
          </div>
          {hike.max_partecipanti && (
            <div>
              <p className="text-gray-500">Max partecipanti</p>
              <p className="font-medium text-gray-800">{hike.max_partecipanti}</p>
            </div>
          )}
          {hike.metadata?.distanza && (
            <div>
              <p className="text-gray-500">Distanza</p>
              <p className="font-medium text-gray-800">{hike.metadata.distanza}</p>
            </div>
          )}
          {hike.metadata?.dislivello && (
            <div>
              <p className="text-gray-500">Dislivello</p>
              <p className="font-medium text-gray-800">{hike.metadata.dislivello}</p>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
          {hike.descrizione}
        </div>
      </div>

      {/* Cost breakdown - separate card */}
      {hasCosts && (
        <div className="border-t border-gray-200 p-4">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Quote</h2>
          {(hike.quota_assicurazione || hike.quota_pernotto_non_cai) ? (
            <>
              {/* Two-column layout with vertical separator */}
              <div className="flex items-stretch">
                {/* Left column - Soci CAI */}
                <div className="flex-1 pr-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Soci CAI</p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {hike.quota_cai && (
                      <div>
                        <p className="text-xs text-gray-500">Quota CAI</p>
                        <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                      </div>
                    )}
                    {hike.quota_pernotto && (
                      <div>
                        <p className="text-xs text-gray-500">Pernotto</p>
                        <p className="font-semibold text-blue-600">€{hike.quota_pernotto.toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Totale Soci CAI</p>
                    <p className="text-lg font-bold text-blue-600">
                      €{((hike.quota_cai || 0) + (hike.quota_pernotto || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>
                {/* Vertical separator */}
                <div className="w-px bg-gray-200"></div>
                {/* Right column - Non Soci */}
                <div className="flex-1 pl-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Non Soci</p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {hike.quota_cai && (
                      <div>
                        <p className="text-xs text-gray-500">Quota CAI</p>
                        <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                      </div>
                    )}
                    {hike.quota_assicurazione && (
                      <div>
                        <p className="text-xs text-gray-500">Assicurazione</p>
                        <p className="font-semibold text-blue-600">€{hike.quota_assicurazione.toFixed(2)}</p>
                      </div>
                    )}
                    {(hike.quota_pernotto_non_cai || hike.quota_pernotto) && (
                      <div>
                        <p className="text-xs text-gray-500">Pernotto</p>
                        <p className="font-semibold text-blue-600">€{(hike.quota_pernotto_non_cai || hike.quota_pernotto).toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Totale Non Soci</p>
                    <p className="text-lg font-bold text-blue-600">
                      €{((hike.quota_cai || 0) + (hike.quota_assicurazione || 0) + (hike.quota_pernotto_non_cai || hike.quota_pernotto || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {hike.quota_cai && (
                  <div>
                    <p className="text-xs text-gray-500">Quota CAI</p>
                    <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                  </div>
                )}
                {hike.quota_pernotto && (
                  <div>
                    <p className="text-xs text-gray-500">Pernotto</p>
                    <p className="font-semibold text-blue-600">€{hike.quota_pernotto.toFixed(2)}</p>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">Totale</p>
                <p className="text-lg font-bold text-green-600">
                  €{((hike.quota_cai || 0) + (hike.quota_pernotto || 0)).toFixed(2)}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Categories */}
      {hike.categories && hike.categories.length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <h2 className="text-base font-semibold text-gray-800 mb-2">Categorie</h2>
          <div className="flex flex-wrap gap-1.5">
            {hike.categories.map((category, index) => (
              <span
                key={index}
                className="px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Registration button */}
      <div className="border-t border-gray-200 p-4">
        <a
          href={registerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center py-2.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700 transition"
        >
          Iscriviti
        </a>
      </div>
    </div>
  );
}
